import { z } from 'zod';
import { userRoleSchema } from './index.js';
// ── shops ────────────────────────────────────────────────────────────────────
export const shopTypeSchema = z.enum(['HQ', 'SHOP', 'ONLINE']);
export const shopCreateSchema = z.object({
    code: z.string().min(1).max(8).regex(/^[A-Z0-9]+$/, 'Uppercase letters and digits only'),
    name: z.string().min(1).max(120),
    type: shopTypeSchema.default('SHOP'),
    // The head-office retail counter — where HQ-level staff default to selling and
    // where everyone (not only sales girls) earns a pool share. At most one per company.
    isHeadOffice: z.boolean().optional(),
    address: z.string().max(500).optional(),
    latitude: z.number().gte(-90).lte(90).optional().nullable(),
    longitude: z.number().gte(-180).lte(180).optional().nullable(),
    phone: z.string().max(40).optional(),
    invoicePrefix: z.string().max(20).optional(),
});
export const shopUpdateSchema = shopCreateSchema.partial().extend({
    isActive: z.boolean().optional(),
});
// ── users ────────────────────────────────────────────────────────────────────
export const userCreateSchema = z.object({
    // Email is the login identifier (unique); normalize before it hits the DB so
    // " Foo@X.com " and "foo@x.com" can't both exist.
    email: z.string().trim().toLowerCase().email(),
    name: z.string().min(1).max(120),
    phone: z.string().max(40).optional(),
    role: userRoleSchema,
    homeShopId: z.string().nullable().optional(),
    password: z.string().min(8).max(128),
    hireDate: z.coerce.date().optional(),
    // Sandbox account. Only honoured when the caller holds `users.set_test_mode`
    // (enforced server-side); ignored otherwise.
    isTest: z.boolean().optional(),
});
export const userUpdateSchema = z.object({
    name: z.string().min(1).max(120).optional(),
    // Email is the login identifier (unique); normalize before it hits the DB so
    // " Foo@X.com " and "foo@x.com" can't both exist.
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email()
        .optional(),
    phone: z.string().max(40).optional(),
    role: userRoleSchema.optional(),
    homeShopId: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
    // Password change handled by a separate endpoint (passwordResetSchema).
    // Sandbox flag — gated by `users.set_test_mode` server-side.
    isTest: z.boolean().optional(),
    // Password change handled by a separate endpoint.
});
// Admin-initiated password reset. The admin sets (or generates) a new password
// and hands it over; resetting also terminates the user's live sessions.
export const passwordResetSchema = z.object({
    password: z.string().min(8).max(128),
});
export const userShopAssignmentSchema = z.object({
    shopId: z.string().min(1),
    effectiveFrom: z.coerce.date(),
    effectiveTo: z.coerce.date().optional().nullable(),
    reason: z.string().max(500).optional(),
});
// ── categories ───────────────────────────────────────────────────────────────
export const fulfillmentStrategySchema = z.enum([
    'STOCK_AT_LOCATION',
    'MADE_TO_ORDER_FACTORY',
    'MADE_TO_ORDER_SUPPLIER',
]);
export const categoryCreateSchema = z.object({
    code: z.string().min(1).max(16).regex(/^[A-Z0-9_]+$/, 'Uppercase letters, digits, underscores'),
    name: z.string().min(1).max(120),
    description: z.string().max(1000).optional().nullable(),
    fulfillmentStrategy: fulfillmentStrategySchema,
    deliveryRequired: z.boolean().default(false),
    // Nullable: the form sends null for the lead time that doesn't apply to the
    // chosen strategy (e.g. STOCK_AT_LOCATION has neither).
    productionLeadTimeDays: z.number().int().min(0).max(365).optional().nullable(),
    supplierLeadTimeDays: z.number().int().min(0).max(365).optional().nullable(),
    tracksStockPerLocation: z.boolean().default(true),
    // Every physical piece in this category carries a hand-written number
    // (imported mattresses). Turns on the Unit numbers page for its products.
    tracksUnitNumbers: z.boolean().default(false),
    // A DELIVERY line in this category is sourced from the warehouse and nowhere
    // else — when the warehouse is short it's reserved against the next arrival
    // rather than taken off a showroom floor.
    fulfilFromWarehouseOnly: z.boolean().default(false),
    displayOrder: z.number().int().min(0).default(0),
    // GL posting accounts (by Account.code). Needed so sales (and COGS on delivery)
    // can post to the ledger. Optional so non-sold categories can still be created.
    glRevenueAccountCode: z.string().max(20).optional().nullable(),
    glCogsAccountCode: z.string().max(20).optional().nullable(),
});
export const categoryUpdateSchema = categoryCreateSchema.partial().extend({
    isActive: z.boolean().optional(),
});
// ── trusted devices (mTLS device identity) ───────────────────────────────────
export const deviceStatusSchema = z.enum(['ACTIVE', 'REVOKED']);
export const deviceRegisterSchema = z.object({
    label: z.string().min(1).max(120),
    location: z.string().min(1).max(120),
    // lowercase hex SHA-256 of the client cert, no colons — as printed by issue-device.sh
    fingerprint: z.string().regex(/^[a-f0-9]{64}$/, 'Lowercase hex SHA-256 (64 chars, no colons)'),
    notes: z.string().max(1000).optional(),
});
export const deviceSetStatusSchema = z.object({
    status: deviceStatusSchema,
});
// Edit a registered device. Every field optional so the same PATCH handles both
// the status toggle (revoke / reactivate) and label/location/notes edits. The
// fingerprint is the cert identity and is never editable.
export const deviceUpdateSchema = z
    .object({
    label: z.string().min(1).max(120),
    location: z.string().min(1).max(120),
    notes: z.string().max(1000).nullable(),
    status: deviceStatusSchema,
})
    .partial();
//# sourceMappingURL=admin.js.map